import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/layout/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import Riders from "./pages/Riders";
import Users from "./pages/Users";
import Branches from "./pages/Branches";
import Financials from "./pages/Financials";
import Promotions from "./pages/Promotions";
import Ratings from "./pages/Ratings";
import B2BClients from "./pages/B2BClients";
import Settings from "./pages/Settings";

// Role-gated route: redirects if user lacks required role
const RoleRoute = ({ children, roles }) => {
  const { user } = useAuth();
  if (!roles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", gap:12, color:"var(--text2)" }}>
      <div className="spinner"/> Loading...
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (!["admin","super_admin","branch_manager"].includes(user.role)) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", flexDirection:"column", gap:12 }}>
        <div style={{ fontSize:48 }}>🚫</div>
        <p style={{ color:"var(--text2)" }}>This account doesn't have dashboard access.</p>
      </div>
    );
  }
  return children;
};

const ADMIN_ONLY = ["admin","super_admin"];
const ALL_ADMINS = ["admin","super_admin","branch_manager"];

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background:"var(--bg3)", color:"var(--text)", border:"1px solid var(--border)", fontFamily:"Syne,sans-serif", fontSize:13 },
            success: { iconTheme: { primary:"var(--green)", secondary:"var(--bg3)" } },
            error:   { iconTheme: { primary:"var(--red)",   secondary:"var(--bg3)" } },
          }}
        />
        <Routes>
          <Route path="/login" element={<Login/>}/>
          <Route path="/" element={<Protected><Layout/></Protected>}>
            <Route index element={<Dashboard/>}/>
            <Route path="orders"     element={<RoleRoute roles={ALL_ADMINS}><Orders/></RoleRoute>}/>
            <Route path="riders"     element={<RoleRoute roles={ALL_ADMINS}><Riders/></RoleRoute>}/>
            <Route path="users"      element={<RoleRoute roles={ALL_ADMINS}><Users/></RoleRoute>}/>
            <Route path="branches"   element={<RoleRoute roles={ALL_ADMINS}><Branches/></RoleRoute>}/>
            <Route path="financials" element={<RoleRoute roles={ALL_ADMINS}><Financials/></RoleRoute>}/>
            <Route path="ratings"    element={<RoleRoute roles={ALL_ADMINS}><Ratings/></RoleRoute>}/>
            <Route path="promotions" element={<RoleRoute roles={ADMIN_ONLY}><Promotions/></RoleRoute>}/>
            <Route path="b2b"        element={<RoleRoute roles={ADMIN_ONLY}><B2BClients/></RoleRoute>}/>
            <Route path="settings"   element={<RoleRoute roles={ALL_ADMINS}><Settings/></RoleRoute>}/>
          </Route>
          <Route path="*" element={<Navigate to="/" replace/>}/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}